// In-card slider (used inside listing cards). Document-level event delegation
// so newly-rendered cards work automatically without per-card init.
// Supports prev/next buttons, dot clicks, AND pointer/touch swipe.
import { qs, qsa, on, delegate } from '../lib/dom.js';

const SWIPE_THRESHOLD = 40; // px

function getActive(slider) { return Number(slider.dataset.active || 0); }
function getCount(slider) { return Number(slider.dataset.count || 0) || qsa('.ap-card-slide', slider).length; }

// Swap data-src into src on a slide's <img> the first time it's needed.
// Lets cards ship to the DOM with only the first image's src populated, so
// the browser doesn't fetch all 4 photos × N cards on initial paint.
function hydrateSlide(slider, idx) {
  const slide = qsa('.ap-card-slide', slider)[idx];
  if (!slide) return;
  const img = qs('img[data-src]', slide);
  if (img) {
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  }
}

function setActive(slider, idx) {
  const count = getCount(slider);
  if (!count) return;
  const active = ((idx % count) + count) % count;
  slider.dataset.active = active;

  // Hydrate the slide we're moving to (and a small lookahead for prev/next).
  hydrateSlide(slider, active);
  hydrateSlide(slider, (active + 1) % count);
  hydrateSlide(slider, (active - 1 + count) % count);

  const track = qs('.ap-listing-card__track', slider);
  if (track) track.style.transform = `translateX(-${active * 100}%)`;

  qsa('.ap-card-slide', slider).forEach((s, i) => s.classList.toggle('is-active', i === active));
  qsa('.ap-card-dot', slider).forEach((d, i) => d.classList.toggle('is-active', i === active));

  const counter = qs('[data-role="card-counter"]', slider);
  if (counter) counter.textContent = active + 1;
}

export function initCardSliders() {
  // Buttons + dots - stop click from triggering the parent <a> link
  delegate(document, 'click', '[data-action="card-prev"]', (e, btn) => {
    e.preventDefault();
    e.stopPropagation();
    const slider = btn.closest('[data-component="card-slider"]');
    if (slider) setActive(slider, getActive(slider) - 1);
  });
  delegate(document, 'click', '[data-action="card-next"]', (e, btn) => {
    e.preventDefault();
    e.stopPropagation();
    const slider = btn.closest('[data-component="card-slider"]');
    if (slider) setActive(slider, getActive(slider) + 1);
  });
  delegate(document, 'click', '.ap-card-dot', (e, btn) => {
    e.preventDefault();
    e.stopPropagation();
    const slider = btn.closest('[data-component="card-slider"]');
    if (slider) setActive(slider, Number(btn.dataset.dot));
  });

  // Swipe (Pointer events - unified touch + mouse drag)
  let activeSlider = null;
  let startX = 0;
  let dx = 0;
  let dragging = false;

  on(document, 'pointerdown', (e) => {
    // Only left-click for mouse; ignore button presses (handled by their own click)
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('[data-action]') || e.target.closest('.ap-card-dot')) return;
    const slider = e.target.closest('[data-component="card-slider"]');
    if (!slider) return;
    activeSlider = slider;
    startX = e.clientX;
    dx = 0;
    dragging = true;
  }, { passive: true });

  on(document, 'pointermove', (e) => {
    if (!dragging) return;
    dx = e.clientX - startX;
  }, { passive: true });

  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    const slider = activeSlider;
    activeSlider = null;
    if (!slider) return;

    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx > 0) setActive(slider, getActive(slider) - 1);
      else setActive(slider, getActive(slider) + 1);

      // Block the next click on this slider so the parent <a> doesn't navigate
      const block = (ce) => {
        ce.preventDefault();
        ce.stopPropagation();
        slider.removeEventListener('click', block, true);
      };
      slider.addEventListener('click', block, true);
      // Safety: clear after a beat in case no click fires
      setTimeout(() => slider.removeEventListener('click', block, true), 350);
    }
  }
  on(document, 'pointerup', endDrag);
  on(document, 'pointercancel', endDrag);
}
